import { useState, useEffect } from "react";
import { AlertTriangle, ChevronRight, Copy, Merge, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";

export default function DuplicatesAlert() {
  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];
  var expandedState = useState(false);
  var confirmState = useState(false);
  var mergingState = useState(false);
  var resultState = useState(null);
  var navigate = useNavigate();

  function fetchDuplicates() {
    api.get("/dashboard/duplicates-alert").then(function(res) {
      if (res.data.total_duplicates > 0) {
        setData(res.data);
      } else {
        setData(null);
      }
    }).catch(function() {});
  }

  useEffect(function() { fetchDuplicates(); }, []);

  async function handleMerge() {
    mergingState[1](true);
    resultState[1](null);
    try {
      var res = await api.post("/clients/cleanup-duplicates");
      resultState[1](res.data);
      confirmState[1](false);
      fetchDuplicates();
    } catch(err) {
      resultState[1]({ error: err.response?.data?.detail || "Erro ao mesclar" });
    } finally {
      mergingState[1](false);
    }
  }

  if (resultState[0] && !data) {
    var r = resultState[0];
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl lg:rounded-2xl p-4 lg:p-6 mb-6 lg:mb-8" data-testid="duplicates-merge-result">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-emerald-800">Duplicados mesclados com sucesso</p>
            <p className="text-xs text-emerald-600 mt-1">
              {r.clients_merged} grupo{r.clients_merged !== 1 ? "s" : ""} mesclado{r.clients_merged !== 1 ? "s" : ""}, {r.duplicates_deleted} registro{r.duplicates_deleted !== 1 ? "s" : ""} removido{r.duplicates_deleted !== 1 ? "s" : ""}, {r.products_moved} produto{r.products_moved !== 1 ? "s" : ""} transferido{r.products_moved !== 1 ? "s" : ""}
            </p>
          </div>
          <button onClick={function() { resultState[1](null); }} className="text-xs text-emerald-700 font-medium hover:underline">OK</button>
        </div>
      </div>
    );
  }

  if (!data || data.total_duplicates === 0) return null;

  var displayed = expandedState[0] ? data.duplicates : data.duplicates.slice(0, 3);

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl lg:rounded-2xl p-4 lg:p-6 mb-6 lg:mb-8" data-testid="duplicates-alert">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-100 rounded-lg">
            <Copy className="w-4 h-4 text-amber-600" />
          </div>
          <h3 className="text-sm lg:text-base font-bold text-amber-800">Cadastros Duplicados</h3>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-200 text-amber-800">{data.total_duplicates}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={function() { confirmState[1](true); }} data-testid="duplicates-merge-button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition-colors">
            <Merge className="w-3.5 h-3.5" />Mesclar Todos
          </button>
          <button onClick={function() { navigate("/clientes"); }}
            className="flex items-center gap-1 text-xs text-amber-700 font-medium hover:underline" data-testid="duplicates-go-clients">
            Gerenciar<ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {confirmState[0] && (
        <div className="bg-white border border-amber-300 rounded-lg p-3 mb-3" data-testid="duplicates-merge-confirm">
          <p className="text-xs text-gray-700 mb-2">
            Isso vai mesclar <strong>{data.total_duplicates} grupo{data.total_duplicates !== 1 ? "s" : ""}</strong> de duplicados. Para cada grupo, o sistema mantem a ficha mais antiga e transfere todos os produtos das fichas duplicadas para ela. As fichas extras serao excluidas.
          </p>
          <div className="flex gap-2 justify-end">
            <button onClick={function() { confirmState[1](false); }} data-testid="duplicates-merge-cancel"
              className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50">Cancelar</button>
            <button onClick={handleMerge} disabled={mergingState[0]} data-testid="duplicates-merge-confirm-button"
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1.5">
              {mergingState[0] ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Mesclando...</> : "Confirmar Mesclagem"}
            </button>
          </div>
        </div>
      )}

      {resultState[0] && resultState[0].error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 mb-3">
          <p className="text-xs text-red-700">{resultState[0].error}</p>
        </div>
      )}

      <div className="space-y-2">
        {displayed.map(function(dup, idx) {
          return (
            <div key={idx} className="flex items-center gap-3 p-2.5 bg-white/70 rounded-lg border border-amber-100" data-testid={"duplicate-item-" + idx}>
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800">
                  {dup.doc_type} {dup.doc_value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")} - {dup.count} registros
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {dup.clients.map(function(c) { return c.name; }).join(", ")}
                </p>
              </div>
              <button onClick={function() { navigate("/clientes/" + dup.clients[0].id); }}
                className="text-xs text-[#3B82F6] font-medium hover:underline flex-shrink-0 whitespace-nowrap"
                data-testid={"duplicate-view-" + idx}>
                Ver ficha
              </button>
            </div>
          );
        })}
      </div>
      {data.duplicates.length > 3 && (
        <button onClick={function() { expandedState[1](function(v) { return !v; }); }}
          className="mt-2 text-xs text-amber-700 font-medium hover:underline" data-testid="duplicates-toggle-expand">
          {expandedState[0] ? "Ver menos" : "Ver todos (" + data.total_duplicates + ")"}
        </button>
      )}
    </div>
  );
}
